/**
 * Edit a whole paragraph as text; its lines re-flow in its own font,
 * margins, line spacing and alignment.
 */

import { useState } from 'react'
import type { Paragraph } from '../api'

interface Props {
  paragraph: Paragraph
  busy?: boolean
  onApply: (text: string) => void
  onCancel: () => void
}

const ALIGN: Record<Paragraph['align'], string> = {
  left: 'left-aligned', center: 'centred', right: 'right-aligned', justify: 'justified',
}

export function RewrapDialog({ paragraph, busy, onApply, onCancel }: Props) {
  const [text, setText] = useState(paragraph.text)
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="rewrap-title">
      <form className="modal__panel rewrap" onSubmit={(e) => { e.preventDefault(); onApply(text) }}>
        <h2 className="modal__title" id="rewrap-title">Edit paragraph</h2>
        <div className="modal__body">
          <textarea
            className="rewrap__text"
            value={text}
            autoFocus
            rows={Math.min(14, Math.max(5, Math.ceil(text.length / 70) + 1))}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // The same rule as the button: Ctrl+Enter on blank or unchanged
              // text used to delete the paragraph, or record a no-op.
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                if (!busy && text.trim() && text !== paragraph.text) onApply(text)
              }
              if (e.key === 'Escape') onCancel()
            }}
          />
          <p className="modal__hint">
            The lines re-flow in this paragraph&rsquo;s own font, margins and line
            spacing, and stay {ALIGN[paragraph.align] ?? 'aligned'}. Press Enter for a
            line break; Ctrl+Enter applies. If the paragraph grows into what
            follows it, Odie says so &mdash; nothing below it moves.
          </p>
        </div>
        <div className="modal__footer">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn--primary"
                  disabled={busy || !text.trim() || text === paragraph.text}>
            {busy ? 'Rewrapping…' : 'Rewrap'}
          </button>
        </div>
      </form>
    </div>
  )
}
